import { useEffect, useMemo, useState } from "react";
import { Copy, PencilSimple, Plus, Spinner, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const ENAMED_REFERENCE = [
  { knowledge_areas: ["Clínica Médica"], question_count: 20 },
  { knowledge_areas: ["Cirurgia Geral"], question_count: 20 },
  { knowledge_areas: ["Pediatria"], question_count: 20 },
  { knowledge_areas: ["Ginecologia e Obstetrícia"], question_count: 20 },
  { knowledge_areas: ["Medicina da Família e Comunidade"], question_count: 7 },
  { knowledge_areas: ["Saúde Coletiva"], question_count: 7 },
  { knowledge_areas: ["Saúde Mental"], question_count: 6 },
];

function editableGroups(style, fallbackArea) {
  if (!style?.groups?.length) {
    return [{ knowledge_areas: [fallbackArea || "Clínica Médica"], question_count: 100 }];
  }
  return style.groups.flatMap((group) => {
    const areas = group.knowledge_areas || [];
    if (areas.length <= 1) {
      return [{
        name: group.name,
        knowledge_areas: areas,
        question_count: Number(group.configured_question_count || group.target || 1),
      }];
    }
    const count = Number(group.configured_question_count || group.target || areas.length);
    const base = Math.floor(count / areas.length);
    return areas.map((area, index) => ({
      knowledge_areas: [area],
      question_count: base + (index < count % areas.length ? 1 : 0),
    })).filter((item) => item.question_count > 0);
  });
}

export function ExamStyleEditorDialog({
  open,
  onOpenChange,
  style = null,
  taxonomy = null,
  defaultSchoolId = "",
  onSaved,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [questionCount, setQuestionCount] = useState(100);
  const [schoolId, setSchoolId] = useState("");
  const [groups, setGroups] = useState([]);
  const [copyPerception, setCopyPerception] = useState(false);
  const [saving, setSaving] = useState(false);
  const areas = useMemo(() => taxonomy?.knowledge_areas || [], [taxonomy]);
  const schools = useMemo(() => taxonomy?.schools || [], [taxonomy]);
  const canCreateGlobal = Boolean(taxonomy?.can_create_global_questions);
  const editingExisting = Boolean(style && !style.builtin && !style.copy_as_new);

  useEffect(() => {
    if (!open) return;
    const reference = style?.id === "enamed-2025-2026" && style?.copy_as_new
      ? ENAMED_REFERENCE.map((item) => ({ ...item, knowledge_areas: [...item.knowledge_areas] }))
      : editableGroups(style, areas[0]);
    setName(style?.copy_as_new ? `${style.name} · personalizado` : (style?.name || ""));
    setDescription(style?.copy_as_new ? "" : (style?.description || ""));
    setQuestionCount(Number(style?.default_question_count || 100));
    setSchoolId(style?.school_id || defaultSchoolId || (!canCreateGlobal && schools.length === 1 ? schools[0].id : ""));
    setGroups(reference);
    setCopyPerception(Boolean(style?.copy_enamed_perception || (style?.copy_as_new && style?.id === "enamed-2025-2026")));
  }, [open, style, areas, schools, defaultSchoolId, canCreateGlobal]);

  const allocated = useMemo(
    () => groups.reduce((total, item) => total + Math.max(0, Number(item.question_count || 0)), 0),
    [groups],
  );

  const setGroup = (index, changes) => {
    setGroups((current) => current.map((item, position) => position === index ? { ...item, ...changes } : item));
  };

  const addGroup = () => {
    const used = new Set(groups.map((item) => item.knowledge_areas?.[0]));
    const nextArea = areas.find((item) => !used.has(item));
    if (!nextArea) return toast.error("Todas as áreas disponíveis já foram adicionadas");
    setGroups((current) => [...current, { knowledge_areas: [nextArea], question_count: 1 }]);
    return undefined;
  };

  const useEnamedReference = () => {
    setQuestionCount(100);
    setGroups(ENAMED_REFERENCE.map((item) => ({ ...item, knowledge_areas: [...item.knowledge_areas] })));
    setCopyPerception(true);
  };

  const save = async () => {
    if (name.trim().length < 2) return toast.error("Informe o nome do modelo");
    if (!canCreateGlobal && !schoolId && schools.length !== 1) return toast.error("Selecione a escola do modelo");
    if (!groups.length) return toast.error("Adicione pelo menos uma área de conhecimento");
    if (allocated !== Number(questionCount)) {
      return toast.error(`A soma das áreas é ${allocated}; o total configurado é ${questionCount}`);
    }
    const selectedAreas = groups.map((item) => item.knowledge_areas?.[0]);
    if (new Set(selectedAreas).size !== selectedAreas.length) return toast.error("Cada área pode aparecer somente uma vez");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        question_count: Number(questionCount),
        school_id: schoolId || null,
        copy_enamed_perception: copyPerception,
        groups: groups.map((item) => ({
          name: item.knowledge_areas[0],
          knowledge_areas: item.knowledge_areas,
          question_count: Number(item.question_count),
          balance_subareas: false,
        })),
      };
      const response = editingExisting
        ? await api.put(`/exam-styles/${encodeURIComponent(style.id)}`, payload)
        : await api.post("/exam-styles", payload);
      toast.success(editingExisting ? "Modelo atualizado" : "Modelo criado e disponível na montagem inteligente");
      onSaved?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none max-w-[820px] w-[95vw] max-h-[90dvh] overflow-y-auto"
        aria-describedby="exam-style-editor-description"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black">
            {editingExisting ? "Editar modelo de prova" : "Criar modelo de prova"}
          </DialogTitle>
        </DialogHeader>
        <p id="exam-style-editor-description" className="text-sm text-muted-foreground">
          Configure uma distribuição reutilizável; o sorteio selecionará exatamente a quantidade prevista para cada área.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2"><span className="label-eyebrow block mb-1">nome do modelo</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Residência UNIVAG · 80 questões" className="rounded-none" data-testid="exam-style-name" />
          </label>
          <label><span className="label-eyebrow block mb-1">total de questões</span>
            <Input type="number" min={1} max={200} value={questionCount} onChange={(event) => setQuestionCount(event.target.value)} className="rounded-none" data-testid="exam-style-question-count" />
          </label>
          <label><span className="label-eyebrow block mb-1">disponibilidade</span>
            <select value={schoolId} onChange={(event) => setSchoolId(event.target.value)} className="w-full h-10 border border-border bg-background px-3 text-sm" data-testid="exam-style-school">
              {canCreateGlobal && <option value="">Global · todas as escolas</option>}
              {!canCreateGlobal && schools.length !== 1 && <option value="">Selecione a escola</option>}
              {schools.map((school) => <option key={school.id} value={school.id}>{school.name}</option>)}
            </select>
          </label>
          <label className="sm:col-span-2"><span className="label-eyebrow block mb-1">descrição opcional</span>
            <Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Objetivo ou observações do modelo" className="rounded-none" />
          </label>
        </div>

        <div className="border border-border">
          <div className="p-4 border-b border-border flex flex-wrap gap-3 items-center justify-between">
            <div><div className="label-eyebrow">distribuição temática</div><div className="font-display text-xl font-bold">Questões por área</div></div>
            <Button type="button" variant="outline" onClick={useEnamedReference} className="rounded-none text-xs" data-testid="exam-style-copy-enamed">
              <Copy className="mr-2" />Usar ENAMED como base
            </Button>
          </div>
          <div className="p-4 space-y-3">
            {groups.map((group, index) => (
              <div key={`${index}-${group.knowledge_areas?.[0]}`} className="grid grid-cols-[minmax(0,1fr)_90px_40px] gap-2 items-center" data-testid="exam-style-group">
                <select value={group.knowledge_areas?.[0] || ""} onChange={(event) => setGroup(index, { knowledge_areas: [event.target.value] })} className="h-10 min-w-0 border border-border bg-background px-3 text-sm">
                  {areas.map((area) => <option key={area} value={area}>{area}</option>)}
                </select>
                <Input type="number" min={1} max={200} value={group.question_count} onChange={(event) => setGroup(index, { question_count: event.target.value })} className="rounded-none" />
                <Button type="button" size="icon" variant="outline" className="rounded-none" onClick={() => setGroups((current) => current.filter((_item, position) => position !== index))} disabled={groups.length === 1} aria-label="Remover área"><Trash /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="rounded-none" onClick={addGroup} disabled={groups.length >= areas.length} data-testid="exam-style-add-area"><Plus className="mr-2" />Adicionar área</Button>
          </div>
          <div className={`px-4 py-3 border-t border-border text-sm font-bold ${allocated === Number(questionCount) ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid="exam-style-allocation-total">
            {allocated} de {questionCount} questões distribuídas
          </div>
        </div>

        <label className="flex gap-3 border border-border p-3 text-sm">
          <input type="checkbox" checked={copyPerception} onChange={(event) => setCopyPerception(event.target.checked)} />
          <span><b>Copiar as 9 questões oficiais de percepção do ENAMED</b><br /><span className="text-muted-foreground">Disponível quando o cartão selecionado comportar questões de percepção.</span></span>
        </label>
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" className="rounded-none" onClick={save} disabled={saving || allocated !== Number(questionCount)} data-testid="exam-style-save">
            {saving && <Spinner className="mr-2 animate-spin" />}{editingExisting ? "Salvar alterações" : "Criar modelo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ExamStyleManagementBlock() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [taxonomyResponse, stylesResponse] = await Promise.all([
        api.get("/questions/taxonomy"),
        api.get("/exam-styles?question_count=100"),
      ]);
      setTaxonomy(taxonomyResponse.data);
      setStyles(stylesResponse.data.items || []);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (style) => {
    if (!window.confirm(`Excluir o modelo “${style.name}”? As provas já criadas continuarão preservadas.`)) return;
    try {
      await api.delete(`/exam-styles/${encodeURIComponent(style.id)}`);
      toast.success("Modelo excluído; as provas anteriores foram preservadas");
      await load();
    } catch (error) {
      toast.error(fmtError(error));
    }
  };

  return (
    <section className="border border-border bg-card mb-6" data-testid="exam-style-management">
      <header className="border-b border-border px-6 py-4 flex flex-wrap gap-3 items-center justify-between">
        <div><div className="label-eyebrow">montagem inteligente · modelos reutilizáveis</div><div className="font-display text-2xl font-bold tracking-tight mt-1">Modelos de prova</div></div>
        <Button type="button" className="rounded-none" onClick={() => { setEditing(null); setEditorOpen(true); }} disabled={!taxonomy} data-testid="exam-style-create"><Plus className="mr-2" />Criar modelo</Button>
      </header>
      <div className="p-6">
        <p className="text-sm text-muted-foreground mb-5">Defina quantas questões devem ser sorteadas em cada área. Os modelos aparecem em Provas → Nova prova com questões → Montagem inteligente.</p>
        {loading ? <div className="text-sm text-muted-foreground">Carregando modelos…</div> : (
          <div className="grid lg:grid-cols-2 gap-4">
            {styles.map((style) => (
              <article key={style.id} className="border border-border p-4" data-testid={`exam-style-${style.id}`}>
                <div className="flex gap-3 justify-between items-start"><div><div className="font-display text-lg font-bold">{style.name}</div><div className="text-xs text-muted-foreground mt-1">{style.builtin ? "Modelo padrão do sistema" : `${style.default_question_count} questões · ${style.school_id ? (taxonomy?.schools || []).find((school) => school.id === style.school_id)?.name || "Escola" : "Todas as escolas"}`}</div></div><span className="border px-2 py-1 text-[10px] uppercase">{style.builtin ? "Padrão" : "Personalizado"}</span></div>
                {!!style.groups?.length && <div className="mt-3 space-y-1 text-xs">{style.groups.map((group) => <div key={group.id} className="flex gap-2 justify-between"><span>{group.name}</span><b>{group.configured_question_count}</b></div>)}</div>}
                <div className="flex flex-wrap gap-2 mt-4">
                  {!!style.groups?.length && <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => { setEditing({ ...style, copy_as_new: true }); setEditorOpen(true); }}><Copy className="mr-2" />Duplicar</Button>}
                  {style.can_edit && <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => { setEditing(style); setEditorOpen(true); }}><PencilSimple className="mr-2" />Editar</Button>}
                  {style.can_delete && <Button type="button" variant="outline" size="sm" className="rounded-none text-destructive" onClick={() => remove(style)}><Trash className="mr-2" />Excluir</Button>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <ExamStyleEditorDialog open={editorOpen} onOpenChange={setEditorOpen} style={editing} taxonomy={taxonomy} onSaved={load} />
    </section>
  );
}
